import React from "react";
import { Shield, CheckCircle, FileText, Award, Users, Lock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const Compliance = () => {
  const certifications = [
    {
      title: "HIPAA Compliance Certification",
      description: "Certified in Health Insurance Portability and Accountability Act compliance",
      status: "Current",
      date: "2024"
    },
    {
      title: "DOT Commercial Vehicle Certification",
      description: "Department of Transportation certified vehicles and operations",
      status: "Current", 
      date: "2024"
    },
    {
      title: "State Transportation License",
      description: "Michigan state-issued medical transportation license",
      status: "Current",
      date: "2024"
    },
    {
      title: "Commercial Insurance Coverage",
      description: "Comprehensive commercial liability and passenger insurance",
      status: "Current",
      date: "2024"
    },
    {
      title: "Background Check Certification", 
      description: "All drivers undergo thorough background screening",
      status: "Ongoing",
      date: "Continuous"
    },
    {
      title: "Vehicle Safety Inspections",
      description: "Regular safety inspections and maintenance certifications",
      status: "Current",
      date: "Monthly"
    }
  ];

  const complianceAreas = [
    {
      icon: <Shield className="h-8 w-8 text-blue-600" />,
      title: "HIPAA Privacy & Security",
      description: "Comprehensive protection of Protected Health Information (PHI)",
      features: [
        "Staff HIPAA training and certification",
        "Encrypted data transmission and storage",
        "Signed Business Associate Agreements",
        "Regular privacy audits and assessments",
        "Incident response procedures"
      ]
    },
    {
      icon: <FileText className="h-8 w-8 text-green-600" />,
      title: "Transportation Regulations",
      description: "Full compliance with federal and state transportation requirements",
      features: [
        "DOT vehicle certifications",
        "Driver qualification standards",
        "Hours of service compliance",
        "Vehicle maintenance records",
        "Safety inspection documentation"
      ]
    },
    {
      icon: <Users className="h-8 w-8 text-purple-600" />,
      title: "Employment Standards", 
      description: "Rigorous hiring and training standards for all personnel",
      features: [
        "Comprehensive background checks",
        "Drug and alcohol screening",
        "Professional driver training",
        "Customer service certification", 
        "Ongoing education requirements"
      ]
    },
    {
      icon: <Lock className="h-8 w-8 text-red-600" />,
      title: "Data Security",
      description: "Advanced cybersecurity measures to protect client information",
      features: [
        "SSL encryption for all data transfer",
        "Secure server infrastructure",
        "Access controls and authentication",
        "Regular security updates",
        "Backup and recovery procedures"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Compliance & Certifications</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Quality Transport Services maintains the highest standards of compliance, safety, and 
            certification to ensure reliable and secure medical transportation services.
          </p>
        </div>

        {/* Compliance Overview */}
        <Card className="mb-12 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Award className="h-6 w-6 text-green-600" />
              <span>Veteran-Owned, Fully Compliant Transportation</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              As a veteran-owned and family-operated business, we understand the importance of trust, 
              reliability, and following proper procedures. We maintain comprehensive compliance with 
              all applicable federal, state, and industry regulations.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">100%</div>
                <div className="text-sm text-gray-600">HIPAA Compliant</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">Licensed</div>
                <div className="text-sm text-gray-600">State & Federal</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">Insured</div>
                <div className="text-sm text-gray-600">Comprehensive Coverage</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compliance Areas */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Compliance Areas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {complianceAreas.map((area, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-3">
                    {area.icon}
                    <CardTitle className="text-xl">{area.title}</CardTitle>
                  </div>
                  <p className="text-gray-600">{area.description}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {area.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Certifications Grid */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Current Certifications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <Card key={index} className="border-l-4 border-blue-500">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">{cert.title}</CardTitle>
                    <Badge variant={cert.status === 'Current' ? 'default' : 'secondary'} className="bg-green-100 text-green-800">
                      {cert.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-3">{cert.description}</p>
                  <div className="text-xs text-gray-500">
                    <strong>Date:</strong> {cert.date}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* HIPAA Business Associate */}
        <section className="mb-12">
          <Card className="border-purple-200 bg-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-6 w-6 text-purple-600" />
                <span>HIPAA Business Associate</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">What This Means for Healthcare Providers:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• We can serve as your HIPAA-compliant transportation partner</li>
                    <li>• Signed Business Associate Agreements available</li>
                    <li>• Full compliance with PHI handling requirements</li>
                    <li>• Regular compliance audits and documentation</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">For Patients:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Your health information is protected at the highest level</li>
                    <li>• Information only shared as authorized by you</li>
                    <li>• Secure handling of all medical transport data</li>
                    <li>• Right to privacy maintained throughout service</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Regulatory Oversight */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Regulatory Oversight</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="text-center">
              <CardContent className="p-6">
                <div className="mb-4">
                  <Shield className="h-12 w-12 text-blue-600 mx-auto" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Federal DOT</h3>
                <p className="text-gray-600 text-sm">
                  Department of Transportation safety regulations and vehicle standards
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-6">
                <div className="mb-4">
                  <FileText className="h-12 w-12 text-green-600 mx-auto" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">State of Michigan</h3>
                <p className="text-gray-600 text-sm">
                  State medical transportation licensing and oversight
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-6">
                <div className="mb-4">
                  <Lock className="h-12 w-12 text-purple-600 mx-auto" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">HHS Office for Civil Rights</h3>
                <p className="text-gray-600 text-sm">
                  HIPAA compliance monitoring and privacy protection oversight
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Quality Assurance */}
        <section className="mb-12">
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CheckCircle className="h-6 w-6 text-amber-600" />
                <span>Continuous Quality Assurance</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Regular Audits & Reviews:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Monthly safety inspections</li>
                    <li>• Quarterly compliance audits</li>
                    <li>• Annual regulatory reviews</li>
                    <li>• Ongoing staff training assessments</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Documentation & Records:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Complete maintenance records</li>
                    <li>• Driver qualification files</li>
                    <li>• Training completion certificates</li>
                    <li>• Insurance and bonding documentation</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Contact for Verification */}
        <div className="bg-blue-900 text-white rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">Verify Our Credentials</h3>
          <p className="text-blue-100 mb-6">
            Healthcare providers and partners can request copies of our certifications, 
            licenses, and compliance documentation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:(269) 248-3514" className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
              Call (269) 248-3514
            </a>
            <a href="mailto:qualitytransportservices42@gmail.com" className="border border-white text-white hover:bg-white hover:text-blue-900 px-6 py-3 rounded-lg font-semibold transition-colors">
              Request Documentation
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Compliance;